import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

/**
 * Reusable Pagination Component - Compact Version
 */
const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    maxVisiblePages = 5
}) => {
    if (totalPages <= 1) return null;

    const renderPageButtons = () => {
        const buttons = [];

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => onPageChange(i)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${currentPage === i
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                            : 'text-gray-600 hover:bg-gray-100 border border-gray-200 bg-white'
                            }`}
                    >
                        {i}
                    </button>
                );
            }
        } else {
            const showEllipsisStart = currentPage > 3;
            const showEllipsisEnd = currentPage < totalPages - 2;

            buttons.push(
                <button
                    key={1}
                    onClick={() => onPageChange(1)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${currentPage === 1
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                        : 'text-gray-600 hover:bg-gray-100 border border-gray-200 bg-white'
                        }`}
                >
                    1
                </button>
            );

            if (showEllipsisStart) {
                buttons.push(
                    <div key="ellipsis-start" className="w-7 h-7 flex items-center justify-center text-gray-400">
                        <MoreHorizontal size={12} />
                    </div>
                );
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => onPageChange(i)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${currentPage === i
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                            : 'text-gray-600 hover:bg-gray-100 border border-gray-200 bg-white'
                            }`}
                    >
                        {i}
                    </button>
                );
            }

            if (showEllipsisEnd) {
                buttons.push(
                    <div key="ellipsis-end" className="w-7 h-7 flex items-center justify-center text-gray-400">
                        <MoreHorizontal size={12} />
                    </div>
                );
            }

            buttons.push(
                <button
                    key={totalPages}
                    onClick={() => onPageChange(totalPages)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${currentPage === totalPages
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                        : 'text-gray-600 hover:bg-gray-100 border border-gray-200 bg-white'
                        }`}
                >
                    {totalPages}
                </button>
            );
        }

        return buttons;
    };

    return (
        <div className="flex items-center justify-center gap-1 py-1 px-1">
            <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                <ChevronLeft size={16} />
            </button>

            <div className="flex items-center gap-1">
                {renderPageButtons()}
            </div>

            <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                <ChevronRight size={16} />
            </button>
        </div>
    );
};

export default Pagination;
